import React from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Collapse,
    Divider,
    IconButton,
    Rating,
    Tooltip,
    Typography
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    Language as WebsiteIcon,
    LocationOn as LocationIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    Phone as PhoneIcon
} from '@mui/icons-material';
import {
    PLACE_BUTTON,
    PLACE_CONTENT_HEIGHT,
    PLACE_DESCRIPTION_LIMIT,
    PLACE_PHOTO,
    PLACE_TITLE_LIMIT
} from './placeConstants';

interface PlaceCardProps {
    place: any;
    hasFullAccess: boolean;
    photoIndex: number;
    onNavigatePhoto: (direction: 'prev' | 'next', totalPhotos: number) => void;
    isDescriptionExpanded: boolean;
    onToggleDescription: () => void;
    onEdit: () => void;
    onDelete: () => void;
    t: TranslateFn;
}

type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

const getCategoryIcon = (type: string) => {
    const iconMap: { [key: string]: string } = {
        food: '/images/foodCategory.svg',
        shopping: '/images/shoppingCategory.svg',
        attraction: '/images/attractionCategory.svg',
        services: '/images/servicesCategory.svg',
        emergency: '/images/emergencyCategory.svg'
    };
    return iconMap[type] || '/images/shoppingCategory.svg';
};

const formatAddress = (address: any) => {
    const parts = [
        address?.house_number,
        address?.road,
        address?.town,
        address?.city,
        address?.state,
        address?.country,
        address?.postcode
    ].filter(Boolean);
    return parts.join(', ');
};

const truncateText = (text: string, limit: number): string => {
    if (text.length <= limit) return text;
    const truncated = text.substring(0, limit);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    return lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated;
};

const truncateTitle = (text: string, limit: number): string => {
    if (text.length <= limit) return text;
    const truncated = text.substring(0, limit);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    return (lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated) + '...';
};

const PlacePhotoCarousel: React.FC<{
    photos: any[];
    currentPhotoIndex: number;
    category: string;
    onPrev: () => void;
    onNext: () => void;
}> = ({ photos, currentPhotoIndex, category, onPrev, onNext }) => {
    const totalPhotos = photos.length;
    const currentPhoto = photos[currentPhotoIndex];

    return (
        <Box
            sx={{
                position: 'relative',
                width: PLACE_PHOTO.width,
                height: PLACE_PHOTO.height,
                flexShrink: 0
            }}
        >
            <Avatar
                src={currentPhoto?.url}
                variant="rounded"
                sx={{ width: '100%', height: '100%', borderRadius: 2, objectFit: 'cover' }}
            />
            {totalPhotos > 1 && (
                <>
                    <IconButton
                        onClick={onPrev}
                        sx={{
                            position: 'absolute',
                            left: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                            width: 24,
                            height: 24
                        }}
                    >
                        <PrevIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={onNext}
                        sx={{
                            position: 'absolute',
                            right: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                            width: 24,
                            height: 24
                        }}
                    >
                        <NextIcon fontSize="small" />
                    </IconButton>
                </>
            )}
            <Box
                component="img"
                src={getCategoryIcon(category)}
                alt={category}
                sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    objectFit: 'contain'
                }}
            />
        </Box>
    );
};

const PlaceContactActions: React.FC<{
    contact?: any;
    mapsUrl?: string;
    t: TranslateFn;
}> = ({ contact, mapsUrl, t }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                ml: { xs: 0, sm: 'auto' },
                width: { xs: '100%', sm: 'auto' },
                alignItems: { xs: 'flex-start', sm: 'flex-end' }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    width: '100%'
                }}
            >
                {contact?.phone && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PhoneIcon fontSize="small" />}
                        href={`tel:${String(contact.phone).replace(/[^+\d]/g, '')}`}
                        sx={{
                            textTransform: 'none',
                            borderRadius: PLACE_BUTTON.borderRadius,
                            minWidth: PLACE_BUTTON.minWidth,
                            height: PLACE_BUTTON.height
                        }}
                    >
                        {t('contacts.phone')}
                    </Button>
                )}
                {contact?.website && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<WebsiteIcon fontSize="small" />}
                        href={contact.website}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            textTransform: 'none',
                            borderRadius: PLACE_BUTTON.borderRadius,
                            minWidth: PLACE_BUTTON.minWidth,
                            height: PLACE_BUTTON.height
                        }}
                    >
                        {t('contacts.website')}
                    </Button>
                )}
            </Box>
            {mapsUrl && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LocationIcon fontSize="small" />}
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener"
                    sx={{
                        textTransform: 'none',
                        borderRadius: PLACE_BUTTON.borderRadius,
                        width: '100%',
                        justifyContent: 'center',
                        height: PLACE_BUTTON.height
                    }}
                >
                    {t('contacts.viewOnGoogleMaps')}
                </Button>
            )}
        </Box>
    );
};

const PlaceNotes: React.FC<{
    notes: string;
    isExpanded: boolean;
    onToggle: () => void;
}> = ({ notes, isExpanded, onToggle }) => {
    const shouldTruncate = notes.length > PLACE_DESCRIPTION_LIMIT;
    return (
        <Box sx={{ mt: 0.5 }}>
            <Collapse in={isExpanded || !shouldTruncate}>
                <Typography
                    variant="body2"
                    sx={{ fontSize: '0.875rem', lineHeight: 1.4, color: 'text.secondary' }}
                >
                    {isExpanded ? notes : truncateText(notes, PLACE_DESCRIPTION_LIMIT)}
                </Typography>
            </Collapse>
            {shouldTruncate && (
                <Button
                    size="small"
                    onClick={onToggle}
                    startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{
                        mt: 0.5,
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        minHeight: 'auto',
                        padding: '2px 8px'
                    }}
                >
                    {isExpanded ? 'Show less' : 'Show more'}
                </Button>
            )}
        </Box>
    );
};

const PlaceProfileInline: React.FC<{ profile: any }> = ({ profile }) => (
    <Box sx={{ mt: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Divider sx={{ my: 0.5 }} />
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1,
                backgroundColor: 'action.hover',
                borderRadius: 1,
                width: '100%',
                flexShrink: 0
            }}
        >
            <Avatar src={profile?.photo?.url} sx={{ width: 40, height: 40 }} />
            <Box sx={{ flex: 1 }}>
                <Typography variant="caption" display="block" color="text.secondary" fontSize="0.75rem">
                    {profile?.id}
                </Typography>
                <Typography variant="body2" fontWeight="bold" fontSize="0.875rem">
                    {profile?.name}
                </Typography>
            </Box>
        </Box>
    </Box>
);

const PlaceAdminActions: React.FC<{ onEdit: () => void; onDelete: () => void; hasProfile: boolean; t: TranslateFn }> = ({ onEdit, onDelete, hasProfile, t }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            mt: 1,
            pt: 1,
            borderTop: hasProfile ? 0 : '1px solid',
            borderColor: 'divider',
            alignItems: 'center'
        }}
    >
        <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            onClick={onEdit}
            sx={{
                textTransform: 'none',
                borderRadius: PLACE_BUTTON.borderRadius,
                minWidth: PLACE_BUTTON.minWidth,
                height: PLACE_BUTTON.height
            }}
        >
            {t('places.actions.edit')}
        </Button>
        <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteIcon fontSize="small" />}
            onClick={onDelete}
            sx={{
                textTransform: 'none',
                borderRadius: PLACE_BUTTON.borderRadius,
                minWidth: PLACE_BUTTON.minWidth,
                height: PLACE_BUTTON.height
            }}
        >
            {t('places.actions.delete')}
        </Button>
    </Box>
);

export const PlaceCard: React.FC<PlaceCardProps> = ({
    place,
    hasFullAccess,
    photoIndex,
    onNavigatePhoto,
    isDescriptionExpanded,
    onToggleDescription,
    onEdit,
    onDelete,
    t
}) => {
    const pid = (place as any).id;
    const loc = (place as any).location ?? {};
    const photos: any[] = Array.isArray(loc.photos) ? loc.photos : [];
    const totalPhotos = photos.length || 0;
    const notes = (place as any).notes || '';

    return (
        <Card key={pid} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { paddingBottom: 2 } }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                    <PlacePhotoCarousel
                        photos={photos}
                        currentPhotoIndex={photoIndex}
                        category={loc.type || 'shopping'}
                        onPrev={() => onNavigatePhoto('prev', totalPhotos)}
                        onNext={() => onNavigatePhoto('next', totalPhotos)}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 1, height: `${PLACE_CONTENT_HEIGHT}px` }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: 0.5,
                                        minWidth: 0,
                                        flex: 1
                                    }}
                                >
                                    <Tooltip title={loc.name || t('places.fields.name.unnamed')} arrow>
                                        <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                            {truncateTitle(
                                                loc.name || t('places.fields.name.unnamed'),
                                                PLACE_TITLE_LIMIT
                                            )}
                                        </Typography>
                                    </Tooltip>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Rating value={Number(loc.rating ?? 0)} readOnly size="small" />
                                        <Typography variant="body2" fontSize="0.875rem">
                                            {Number(loc.rating ?? 0)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <PlaceContactActions contact={loc.contact} mapsUrl={(place as any).google_maps_url} t={t} />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-end', mt: 'auto' }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                fontSize="0.875rem"
                                sx={{
                                    wordWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    maxWidth: { xs: '100%', sm: '65%' },
                                    lineHeight: 1.4
                                }}
                            >
                                {loc.address ? formatAddress(loc.address) : ''}
                            </Typography>
                        </Box>

                        {notes && (
                            <PlaceNotes notes={notes} isExpanded={isDescriptionExpanded} onToggle={onToggleDescription} />
                        )}
                    </Box>
                </Box>

                {hasFullAccess && (place as any).profile && <PlaceProfileInline profile={(place as any).profile} />}

                {hasFullAccess && (
                    <PlaceAdminActions
                        onEdit={onEdit}
                        onDelete={onDelete}
                        hasProfile={Boolean((place as any).profile)}
                        t={t}
                    />
                )}
            </CardContent>
        </Card>
    );
};
